/**
 * CorrespondentLoansTab — the Loans tab on the correspondent detail panel.
 *
 * A pipeline REVIEW surface, not just a list: four stat tiles, monthly volume
 * split by outcome, an outcome/product mix card, then the loan table. The charts
 * are filter controls — clicking a donut segment or a product row filters the
 * table, and the chips mirror that state.
 *
 * Colour: three OUTCOME buckets, which is a status encoding, so the hues come
 * from the status tokens in theme/colors.js plus the brand teal. The order
 * matters — Closed(green) → In flight(teal) → Dead(red) — because green and red
 * adjacent in a stack are indistinguishable under deuteranopia. With teal
 * between them the worst adjacent pair is ΔE 17.5 (deutan) instead of 4.2.
 * teal-500 sits just under 3:1 on white, so every mark that uses it is paired
 * with a visible label (and the table below is the full table view).
 */
import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

import Flx from "../../../components/layout/Flx";
import SearchInput from "../../../components/inputs/SearchInput";
import CorrespondentLoansGrid from "./CorrespondentLoansGrid";
import colors, { teal, status as statusColors } from "../../../theme/colors";
import { T } from "../../SupportScreen/components/supportUi";

/* ------------------------------- outcomes ---------------------------------- */

// The three buckets every loan falls into. `Dead` is the literal loanStatus —
// "Inactive" deliberately stays in `In flight` rather than being folded in here,
// because whether an inactive deal counts as lost is a business call, not a
// display one. Change the predicate below if the desk decides otherwise.
export const OUTCOMES = [
  {
    key: "closed",
    label: "Closed",
    color: statusColors.success.solid,
    tint: statusColors.success.bg,
    fg: statusColors.success.fg,
    match: (l) => l?.loanStatus === "Closed",
  },
  {
    key: "active",
    label: "In flight",
    color: teal[500],
    tint: teal[50],
    fg: teal[700],
    match: (l) => l?.loanStatus !== "Closed" && l?.loanStatus !== "Dead",
  },
  {
    key: "dead",
    label: "Dead",
    color: statusColors.error.solid,
    tint: statusColors.error.bg,
    fg: statusColors.error.fg,
    match: (l) => l?.loanStatus === "Dead",
  },
];

// The both-pipelines feed says the outcome itself (acquisitions close as
// Settled / Boarded and die as Declined / Kicked); the old pre-close rows don't.
const outcomeOf = (l) =>
  l?.outcome || (OUTCOMES.find((o) => o.match(l))?.key ?? "active");

const amountOf = (l) =>
  Number(l?.totalLoanAmount ?? l?.baseLoanAmount ?? 0) || 0;

const productOf = (l) => l?.loanProductType || "Unspecified";

/* ------------------------------- formatters -------------------------------- */

// Compact money for axis ticks and stat values — $1.2M / $184K / $0. The shared
// formatDollar is the full-precision one; a KPI tile wants the short read.
const fmtCompact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) {
    return `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, "")}M`;
  }
  if (Math.abs(n) >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
};

const fmtFull = (v) => `$${(Number(v) || 0).toLocaleString("en-US")}`;

// `expectedClosingDate` is a date-only value — parse the YYYY-MM out of the
// string rather than through Date(), which would shift it a day in west-of-UTC
// timezones and could move a loan into the previous month.
const monthKeyOf = (l) => {
  const raw = l?.keyDate ?? l?.expectedClosingDate;
  if (!raw) return null;
  const m = String(raw).match(/^(\d{4})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}` : null;
};

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const monthLabel = (key) => MONTH_NAMES[Number(key.slice(5, 7)) - 1] || "";
const monthLong = (key) =>
  `${
    [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ][Number(key.slice(5, 7)) - 1]
  } ${key.slice(0, 4)}`;

/* --------------------------------- pieces ---------------------------------- */

function Card({ children, sx }) {
  return (
    <Box
      sx={{
        background: T.card,
        border: `1px solid ${T.line}`,
        borderRadius: "12px",
        boxShadow: T.shadow,
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function CardHead({ title, sub }) {
  return (
    <Flx column g={0.25} sx={{ px: 2.5, pt: 2, pb: 1 }}>
      <Box sx={{ fontSize: "13px", fontWeight: 700, color: T.ink900 }}>
        {title}
      </Box>
      {sub && (
        <Box sx={{ fontSize: "12px", color: T.ink500 }}>{sub}</Box>
      )}
    </Flx>
  );
}

// Stat tile — label (uppercase, muted) / value (tabular, tight) / hint.
function StatTile({ label, value, hint, hintColor }) {
  return (
    <Card sx={{ px: 2, py: 1.75 }}>
      <Flx column g={0.5}>
        <Box
          sx={{
            fontSize: "10.5px",
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: T.ink300,
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: T.ink900,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Box>
        {hint && (
          <Box sx={{ fontSize: "12px", color: hintColor || T.ink500 }}>
            {hint}
          </Box>
        )}
      </Flx>
    </Card>
  );
}

/* ------------------------------ volume chart ------------------------------- */

// Stacked column chart, hand-rolled SVG (no chart dependency in this app beyond
// ag-charts, which is heavier than this needs). Mark specs: columns capped at
// 24px, 4px rounded cap on the TOP segment only and square at the baseline, a
// 2px surface gap between stacked segments, hairline gridlines.
const BAR_MAX_W = 24;
const SEG_GAP = 2;

// A rect with `rx` rounds all four corners, which notches the top segment where
// it meets the one below. The spec is a rounded data-end and a SQUARE baseline,
// so the capped segment is drawn as a path with only its top corners rounded.
const segPath = (x, yTop, w, h, radius) => {
  const r = Math.min(radius, w / 2, h);
  if (r <= 0) return `M${x} ${yTop}h${w}v${h}h${-w}Z`;
  return [
    `M${x} ${yTop + r}`,
    `a${r} ${r} 0 0 1 ${r} ${-r}`,
    `h${w - r * 2}`,
    `a${r} ${r} 0 0 1 ${r} ${r}`,
    `v${h - r}`,
    `h${-w}`,
    "Z",
  ].join("");
};

function VolumeChart({ months, undatedCount }) {
  const H = 230;
  const padL = 52;
  const padR = 8;
  const padT = 10;
  const padB = 28;
  // A viewBox with preserveAspectRatio="none" would distort the text, so the
  // SVG keeps a fixed coordinate width and scales as a whole.
  const W = 640;

  const max = useMemo(() => {
    const peak = Math.max(0, ...months.map((m) => m.total));
    return peak > 0 ? peak * 1.12 : 1;
  }, [months]);

  if (!months.length) {
    return (
      <Box sx={{ px: 2.5, pb: 2.5, fontSize: 13, color: T.ink300 }}>
        No loans carry a closing or settlement date yet, so there's nothing to plot.
      </Box>
    );
  }

  const plotH = H - padT - padB;
  const y = (v) => padT + plotH * (1 - v / max);
  const step = (W - padL - padR) / months.length;
  const bw = Math.min(BAR_MAX_W, step * 0.55);

  // Four ticks, rounded through the compact formatter.
  const ticks = [0, 1, 2, 3, 4].map((i) => (max / 4) * i);

  return (
    <Box sx={{ px: 2.5, pb: 1 }}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Loan volume by closing or settlement month, split by outcome"
        sx={{ width: "100%", height: "auto", display: "block" }}
      >
        {ticks.map((v, i) => (
          <g key={i}>
            <line
              x1={padL}
              y1={y(v)}
              x2={W - padR}
              y2={y(v)}
              stroke={T.line}
              strokeWidth="1"
            />
            <text
              x={padL - 8}
              y={y(v) + 3.5}
              textAnchor="end"
              fontSize="10"
              fill={T.ink300}
            >
              {fmtCompact(v)}
            </text>
          </g>
        ))}

        {months.map((m, i) => {
          const x = padL + step * i + (step - bw) / 2;
          // Stack bottom-up in OUTCOMES order reversed so Closed sits on top —
          // the outcome the reader is looking for reads against the air, and it
          // keeps the rounded cap on the segment that actually ends the column.
          // Drawn bottom-up, so Closed ends up on top — the outcome the reader
          // is looking for reads against the air and gets the rounded cap.
          const stack = [...OUTCOMES].reverse();
          const present = stack.filter((o) => m[o.key] > 0);
          const bottomKey = present[0]?.key;
          const topKey = present[present.length - 1]?.key;
          let cursor = 0;
          return (
            <g key={m.key}>
              {stack.map((o) => {
                const v = m[o.key];
                if (!v) return null;
                const yTop = y(cursor + v);
                const yBottom = y(cursor);
                cursor += v;
                const isTop = o.key === topKey;
                // The surface gap goes BETWEEN segments only — taking it out of
                // the bottom-most one too would float the column off its axis.
                const gap = o.key === bottomKey ? 0 : SEG_GAP;
                const h = Math.max(1, yBottom - yTop - gap);
                return (
                  <Tooltip
                    key={o.key}
                    followCursor
                    title={
                      <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
                        <b>{monthLong(m.key)}</b>
                        <br />
                        {o.label}: {fmtFull(v)}
                        <br />
                        <span style={{ opacity: 0.75 }}>
                          {m.count} loan{m.count === 1 ? "" : "s"} ·{" "}
                          {fmtCompact(m.total)} total
                        </span>
                      </Box>
                    }
                  >
                    <path
                      d={segPath(x, yTop, bw, h, isTop ? 4 : 0)}
                      fill={o.color}
                      style={{ cursor: "pointer" }}
                    />
                  </Tooltip>
                );
              })}
              <text
                x={x + bw / 2}
                y={H - 9}
                textAnchor="middle"
                fontSize="10.5"
                fill={T.ink500}
              >
                {monthLabel(m.key)}
              </text>
            </g>
          );
        })}
      </Box>

      {/* Legend — always present for ≥2 series; identity never color-alone. */}
      <Flx wrap g={2} sx={{ mt: 0.5, px: 0.5 }}>
        {OUTCOMES.map((o) => (
          <Flx key={o.key} ac g={0.75}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                background: o.color,
                flexShrink: 0,
              }}
            />
            <Box sx={{ fontSize: "12px", color: T.ink500 }}>{o.label}</Box>
          </Flx>
        ))}
        {undatedCount > 0 && (
          <Box sx={{ fontSize: "12px", color: T.ink300 }}>
            {undatedCount} not plotted (no closing or settlement date)
          </Box>
        )}
      </Flx>
    </Box>
  );
}

/* --------------------------------- donut ----------------------------------- */

function OutcomeDonut({ counts, total, selected, onSelect }) {
  const size = 150;
  const r = 58;
  const c = size / 2;
  const C = 2 * Math.PI * r;
  let offset = C * 0.25;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Loans by outcome"
      sx={{ width: 150, height: 150, display: "block", mx: "auto" }}
    >
      {OUTCOMES.map((o) => {
        const n = counts[o.key] || 0;
        if (!n || !total) return null;
        const frac = n / total;
        const dim = selected && selected !== o.key;
        // The 3px taken out of the dash IS the surface gap between segments.
        const seg = (
          <circle
            key={o.key}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={o.color}
            strokeWidth="17"
            strokeDasharray={`${frac * C - 3} ${C - frac * C + 3}`}
            strokeDashoffset={offset}
            opacity={dim ? 0.25 : 1}
            style={{ cursor: "pointer", transition: "opacity .15s" }}
            onClick={() => onSelect(selected === o.key ? null : o.key)}
          />
        );
        offset -= frac * C;
        return (
          <Tooltip
            key={o.key}
            followCursor
            title={
              <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
                <b>{o.label}</b> · {n} loan{n === 1 ? "" : "s"} (
                {Math.round((n / total) * 100)}%)
                <br />
                <span style={{ opacity: 0.75 }}>Click to filter the table</span>
              </Box>
            }
          >
            {seg}
          </Tooltip>
        );
      })}
      <text
        x={c}
        y={c - 3}
        textAnchor="middle"
        fontSize="24"
        fontWeight="700"
        fill={T.ink900}
      >
        {total}
      </text>
      <text
        x={c}
        y={c + 15}
        textAnchor="middle"
        fontSize="9.5"
        letterSpacing="0.08em"
        fill={T.ink300}
      >
        LOANS
      </text>
    </Box>
  );
}

/* ------------------------------ product bars ------------------------------- */

function ProductBars({ products, totalVolume, selected, onSelect }) {
  if (!products.length) return null;
  return (
    <Flx column g={0.5} sx={{ px: 2.5, pb: 2 }}>
      {products.map((p) => {
        const pct = totalVolume ? (p.volume / totalVolume) * 100 : 0;
        const active = selected === p.name;
        return (
          <Box
            key={p.name}
            component="button"
            onClick={() => onSelect(active ? null : p.name)}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              border: "none",
              cursor: "pointer",
              font: "inherit",
              borderRadius: "8px",
              px: 1,
              py: 0.875,
              mx: -1,
              background: active ? teal[50] : "transparent",
              "&:hover": { background: active ? teal[50] : T.bg },
            }}
          >
            {/* The bar fill is teal-500, which sits just under 3:1 on white —
                these labels are the required relief, so they always render. */}
            <Flx jb ac g={1} sx={{ mb: 0.625 }}>
              <Box
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: T.ink900,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {p.name}
              </Box>
              <Box
                sx={{
                  fontSize: "12.5px",
                  color: T.ink500,
                  flexShrink: 0,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {p.count} · {fmtCompact(p.volume)}
              </Box>
            </Flx>
            <Box
              sx={{
                height: 6,
                borderRadius: "3px",
                background: T.bg,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${pct}%`,
                  borderRadius: "3px",
                  background: teal[500],
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Flx>
  );
}

/* --------------------------------- chips ----------------------------------- */

function FilterChip({ label, count, active, onClick }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        font: "inherit",
        fontSize: "12.5px",
        fontWeight: active ? 700 : 500,
        cursor: "pointer",
        border: `1px solid ${active ? T.ink900 : T.line}`,
        background: active ? T.ink900 : T.card,
        color: active ? "#fff" : T.ink500,
        borderRadius: "999px",
        px: 1.5,
        py: 0.625,
        whiteSpace: "nowrap",
        "&:hover": { borderColor: active ? T.ink900 : T.ink300 },
      }}
    >
      {label}
      {count != null && (
        <Box component="span" sx={{ ml: 0.5, opacity: 0.65, fontWeight: 600 }}>
          {count}
        </Box>
      )}
    </Box>
  );
}

/* ---------------------------------- tab ------------------------------------ */

export default function CorrespondentLoansTab({ detail, rows = [], loading }) {
  const [outcome, setOutcome] = useState(null); // null | outcome key
  const [product, setProduct] = useState(null); // null | product name
  const [search, setSearch] = useState("");

  const all = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  // ── derived: totals, outcome counts, monthly stack, product mix ──────────
  const stats = useMemo(() => {
    const counts = { closed: 0, active: 0, dead: 0 };
    const volume = { closed: 0, active: 0, dead: 0 };
    let total = 0;
    let largest = null;
    all.forEach((l) => {
      const k = outcomeOf(l);
      const amt = amountOf(l);
      counts[k] += 1;
      volume[k] += amt;
      total += amt;
      if (!largest || amt > amountOf(largest)) largest = l;
    });
    const decided = counts.closed + counts.dead;
    return {
      counts,
      volume,
      total,
      largest,
      decided,
      closeRate: decided ? Math.round((counts.closed / decided) * 100) : null,
      avg: all.length ? total / all.length : 0,
    };
  }, [all]);

  const months = useMemo(() => {
    const byKey = {};
    let undated = 0;
    all.forEach((l) => {
      const key = monthKeyOf(l);
      if (!key) {
        undated += 1;
        return;
      }
      byKey[key] = byKey[key] || {
        key,
        closed: 0,
        active: 0,
        dead: 0,
        total: 0,
        count: 0,
      };
      const amt = amountOf(l);
      byKey[key][outcomeOf(l)] += amt;
      byKey[key].total += amt;
      byKey[key].count += 1;
    });
    return {
      list: Object.values(byKey).sort((a, b) => a.key.localeCompare(b.key)),
      undated,
    };
  }, [all]);

  const products = useMemo(() => {
    const byName = {};
    all.forEach((l) => {
      const name = productOf(l);
      byName[name] = byName[name] || { name, count: 0, volume: 0 };
      byName[name].count += 1;
      byName[name].volume += amountOf(l);
    });
    return Object.values(byName).sort((a, b) => b.volume - a.volume);
  }, [all]);

  // ── the filtered set that reaches the table ──────────────────────────────
  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((l) => {
      if (outcome && outcomeOf(l) !== outcome) return false;
      if (product && productOf(l) !== product) return false;
      if (!q) return true;
      return [
        l?.loanNumber,
        l?.loanName,
        l?.loanStatus,
        l?.loanProductType,
        l?.propertyAddress,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [all, outcome, product, search]);

  const filteredVolume = useMemo(
    () => displayRows.reduce((s, l) => s + amountOf(l), 0),
    [displayRows],
  );

  const anyFilter = !!(outcome || product || search.trim());

  if (!detail?.companyName) {
    return (
      <Card sx={{ p: 2.5, fontSize: 13, color: T.ink300 }}>
        This correspondent has no company name to match loans on.
      </Card>
    );
  }

  if (!loading && !all.length) {
    return (
      <Card sx={{ p: 2.5, fontSize: 13, color: T.ink300 }}>
        No loans attributed to this correspondent yet.
      </Card>
    );
  }

  return (
    <Flx column g={2}>
      {/* ── stat strip ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        <StatTile
          label="Total pipeline"
          value={fmtCompact(stats.total)}
          hint={`${all.length} loans · avg ${fmtCompact(stats.avg)}`}
        />
        <StatTile
          label="Closed volume"
          value={fmtCompact(stats.volume.closed)}
          hint={`${stats.counts.closed} loans closed`}
          hintColor={statusColors.success.fg}
        />
        <StatTile
          label="Close rate"
          value={stats.closeRate == null ? "—" : `${stats.closeRate}%`}
          // Rate is of DECIDED loans (closed + dead). Counting in-flight deals
          // as losses would understate a young pipeline, so they're called out
          // separately rather than folded into the denominator.
          hint={
            stats.decided
              ? `${stats.counts.dead} dead of ${stats.decided} decided · ${stats.counts.active} in flight`
              : `no decided loans yet · ${stats.counts.active} in flight`
          }
        />
        <StatTile
          label="Largest loan"
          value={stats.largest ? fmtCompact(amountOf(stats.largest)) : "—"}
          hint={stats.largest ? productOf(stats.largest) : null}
        />
      </Box>

      {/* ── charts ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", lg: "1.7fr 1fr" },
          alignItems: "start",
        }}
      >
        <Card>
          <CardHead
            title="Pipeline volume by closing or settlement month"
            sub="Monthly loan volume, split by outcome · hover a column for detail"
          />
          <VolumeChart months={months.list} undatedCount={months.undated} />
        </Card>

        <Card>
          <CardHead
            title="Outcome & product mix"
            sub="Click a segment or product to filter the table"
          />
          <Box sx={{ px: 2.5, pb: 1 }}>
            <OutcomeDonut
              counts={stats.counts}
              total={all.length}
              selected={outcome}
              onSelect={setOutcome}
            />
          </Box>
          <ProductBars
            products={products}
            totalVolume={stats.total}
            selected={product}
            onSelect={setProduct}
          />
        </Card>
      </Box>

      {/* ── table ───────────────────────────────────────────────────────── */}
      <Card>
        <Flx jb ac wrap g={1.5} sx={{ px: 2.5, pt: 2, pb: 1.25 }}>
          <Flx column g={0.25}>
            <Box sx={{ fontSize: "13px", fontWeight: 700, color: T.ink900 }}>
              Loan pipeline
            </Box>
            <Box sx={{ fontSize: "12px", color: T.ink500 }}>
              {anyFilter
                ? [
                    outcome && OUTCOMES.find((o) => o.key === outcome)?.label,
                    product,
                    search.trim() ? `“${search.trim()}”` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                : "All submissions from this correspondent"}
            </Box>
          </Flx>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search loan #, name, product…"
            size="small"
            rounded
            sx={{ width: 280 }}
          />
        </Flx>

        <Flx wrap ac g={1} sx={{ px: 2.5, pb: 1.5 }}>
          <FilterChip
            label="All"
            count={all.length}
            active={!outcome}
            onClick={() => setOutcome(null)}
          />
          {OUTCOMES.map((o) => (
            <FilterChip
              key={o.key}
              label={o.label}
              count={stats.counts[o.key]}
              active={outcome === o.key}
              onClick={() => setOutcome(outcome === o.key ? null : o.key)}
            />
          ))}
          {products.length > 1 && (
            <>
              <Box
                sx={{
                  width: "1px",
                  alignSelf: "stretch",
                  background: T.line,
                  mx: 0.5,
                }}
              />
              {products.map((p) => (
                <FilterChip
                  key={p.name}
                  label={p.name}
                  count={p.count}
                  active={product === p.name}
                  onClick={() => setProduct(product === p.name ? null : p.name)}
                />
              ))}
            </>
          )}
          {anyFilter && (
            <Box
              component="button"
              onClick={() => {
                setOutcome(null);
                setProduct(null);
                setSearch("");
              }}
              sx={{
                font: "inherit",
                fontSize: "12.5px",
                fontWeight: 600,
                border: "none",
                background: "none",
                color: colors.action,
                cursor: "pointer",
                px: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Clear filters
            </Box>
          )}
        </Flx>

        {/* The table stays AG Grid (house rule) — this tab owns the search and
            filters above, so the grid's own toolbar is suppressed. */}
        <Box sx={{ px: 2.5, pb: 2 }}>
          <CorrespondentLoansGrid
            rows={displayRows}
            loading={loading}
            showToolbar={false}
          />
        </Box>

        <Flx
          jb
          ac
          wrap
          g={1}
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: `1px solid ${T.line}`,
            fontSize: "12.5px",
            color: T.ink500,
          }}
        >
          <Box>
            Showing {displayRows.length} of {all.length} loans
          </Box>
          {!!displayRows.length && (
            <Box sx={{ fontVariantNumeric: "tabular-nums" }}>
              Filtered volume: {fmtFull(filteredVolume)}
            </Box>
          )}
        </Flx>
      </Card>
    </Flx>
  );
}
